/**
 * Step3CotejaPage.tsx — Página principal del paso P3 Coteja
 *
 * UID-03c | Fase 3 | feat/step3-coteja
 * Implementa: CoverageBar (UI-03), FilterRow pills (UI-04),
 *             Urgencia dropdown a la derecha, ContextBar ·
 *
 * @date 2026-03-06
 */

import { useState, useMemo, useCallback, useEffect, type FC, type CSSProperties } from 'react';
import { toast } from 'sonner';
import { colors, typography, borderRadius, spacing } from '@/design-system/v2';
import { InfoBanner, BannerStrong } from '../shell/InfoBanner';
import { usePermissions } from '@/hooks/usePermissions';
import { CoverageBar } from './CoverageBar';
import { FilterRow } from './FilterRow';
import { SectionDivider } from './SectionDivider';
import { CandidatoCard } from './CandidatoCard';
import {
  WorkspaceShell, BuscadorGlobal, normalizarBusqueda, useSeleccionMapaTabla,
} from '../workspace';
import { MapaApoyoCotejo } from './MapaApoyoCotejo';
import { GlossaryTooltip } from '@/components/ui/GlossaryTooltip';
import type { CotejoItem } from './cotejoItem';
import type { VistaFiltro, UrgenciaNivel } from './types';
import { AlojamientosRTASection } from './AlojamientosRTASection';
import type { ProcessedRecord } from '@/types/export.types';
import {
  aplicarDecisiones, ambitoDe,
  type AlmacenDecisiones, type AmbitoDecision, type ValorDecision,
} from '@/hooks/useDecisionesFila';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface Step3CotejaPageProps {
  municipio: string;
  /** Se conserva en la API aunque la página ya no la pinta (fila 2 la muestra) — T2b. */
  provincia: string;
  ine5: string;
  items: CotejoItem[];
  /**
   * ALBRTA-b1: filas del plan (sin plantillas) para la sección «Alojamientos que el plan no
   * recoge». Sin ellas la sección no se monta.
   */
  registrosPlan?: ProcessedRecord[];
  onConfirm: (confirmed: CotejoItem[]) => void;
  onBack: () => void;
  /**
   * C0-b: almacén de decisiones del asistente (`useDecisionesFila`). Con él y con
   * `onDecidir`, P3 no guarda nada propio: pinta `items` con las decisiones
   * aplicadas y cada «Añadir» o «Ignorar» se escribe en el almacén, que sobrevive
   * a salir de P3. Sin ellos, P3 se comporta como antes (estado local).
   */
  decisiones?: AlmacenDecisiones;
  onDecidir?: (id: string, valor: ValorDecision, ambito: AmbitoDecision) => void;
}


// ─── Estilos ──────────────────────────────────────────────────────────────────

const S: Record<string, CSSProperties> = {
  page: {
    background: colors.bg.primary,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    fontFamily: typography.fontFamily.sans,
    color: colors.fg.primary,
  },
  // Contenedor del WorkspaceShell: ocupa el espacio restante bajo la cabecera.
  shellWrap: {
    flex: 1,
    minHeight: 0,
  },
  // Ranura central (table-primary): el contenido de cotejo scrollea aquí dentro.
  centro: {
    height: '100%',
    overflow: 'auto',
  },
  content: {
    margin: '0 56px',
    paddingBottom: '100px',
  },
  header: {
    paddingTop: '24px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
    marginBottom: '20px',
  },
  title: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.primary,
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.fg.muted,
  },
  empty: {
    textAlign: 'center',
    padding: '48px 0',
    color: colors.fg.muted,
    fontSize: typography.fontSize.sm,
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '10px',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: `1px solid ${colors.border.DEFAULT}`,
  },
  btnBack: {
    padding: '8px 20px', borderRadius: borderRadius.DEFAULT,
    fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.sans,
    fontWeight: typography.fontWeight.medium,
    background: 'transparent', color: colors.fg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    cursor: 'pointer',
  },
  btnConfirm: {
    padding: '8px 24px', borderRadius: borderRadius.DEFAULT,
    fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.sans,
    fontWeight: typography.fontWeight.semibold,
    background: colors.primaryMuted, color: colors.primary,
    border: `1px solid ${colors.primary}40`,
    cursor: 'pointer',
  },
};


// ─── Búsqueda ─────────────────────────────────────────────────────────────────

/** Coincidencia normalizada (NFD, sin acentos, minúsculas) sobre nombre + tipología. */
const coincide = (i: CotejoItem, q: string): boolean =>
  normalizarBusqueda(i.nombre).includes(q) || normalizarBusqueda(i.tipologia).includes(q);

/** Orden en que la lista pinta los grupos de tarjetas (el mismo que el del listado). */
const ORDEN_KIND: ReadonlyArray<CotejoItem['kind']> = ['ausente', 'incluido', 'sobrante'];

// ─── Componente principal ─────────────────────────────────────────────────────

export const Step3CotejaPage: FC<Step3CotejaPageProps> = ({
  municipio, ine5, items: initialItems, registrosPlan, onConfirm, onBack,
  decisiones, onDecidir,
}) => {
  const [itemsLocales, setItems] = useState<CotejoItem[]>(initialItems);
  // C0-b: con almacén, lo que se pinta sale del cotejo + las decisiones guardadas.
  const conAlmacen = decisiones !== undefined && onDecidir !== undefined;
  const items = useMemo(
    () => (conAlmacen && decisiones ? aplicarDecisiones(initialItems, decisiones) : itemsLocales),
    [conAlmacen, initialItems, decisiones, itemsLocales],
  );
  const [vista, setVista] = useState<VistaFiltro>('todos');
  const [urgencias, setUrgencias] = useState<UrgenciaNivel[]>([]);
  const [bannerVisible, setBannerVisible] = useState(true);
  const { canValidate: canEdit } = usePermissions();

  // ── T3 · §6 — buscador global + selección compartida cards↔mapa ──────────
  // Reutiliza useSeleccionMapaTabla (S.UI.P5) sin click-to-set: P3 es cotejo,
  // no edición de coordenadas. BUSCP3 (24-oct-2026, decisión de Luis): en P3 el
  // texto FILTRA la lista, además de seleccionar con coincidencia única. Se suma
  // a la pastilla y a la urgencia (intersección); los totales no cambian.
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const { idSeleccion, seleccionar } = useSeleccionMapaTabla();

  // Vista y urgencia activas, sin el texto: es la base sobre la que busca y filtra.
  const base = useMemo(() => {
    let r = items;
    if (vista !== 'todos') r = r.filter(i => i.kind === vista.replace(/s$/, '') as CotejoItem['kind']);
    if (urgencias.length > 0) {
      r = r.filter(i => urgencias.includes(i.urgencia ?? 'ninguna'));
    }
    return r;
  }, [items, vista, urgencias]);

  // Un texto en blanco no es una búsqueda: ni filtra ni cuenta coincidencias.
  const qActual = normalizarBusqueda(textoBusqueda);
  const filtraPorTexto = qActual !== '';

  // Coincidencias VISIBLES (dentro de la vista y la urgencia activas).
  const buscar = useCallback(
    (texto: string): CotejoItem[] => {
      const q = normalizarBusqueda(texto);
      if (q === '') return [];
      return base.filter((i) => coincide(i, q));
    },
    [base],
  );

  const coincidencias = useMemo(() => buscar(textoBusqueda), [buscar, textoBusqueda]);

  // «✓ nombre» solo si el seleccionado pertenece a las coincidencias actuales.
  const nombreSeleccionado = useMemo(
    () => coincidencias.find((c) => c.id === idSeleccion)?.nombre ?? null,
    [coincidencias, idSeleccion],
  );

  // Coincidencia única → selección automática (guard anti-TOGGLE: seleccionar
  // el id ya activo lo deseleccionaría). Vaciar el texto NUNCA deselecciona (§6).
  const manejarBusqueda = (texto: string) => {
    setTextoBusqueda(texto);
    const m = buscar(texto);
    if (m.length === 1 && m[0].id !== idSeleccion) seleccionar(m[0].id);
  };

  // Intro: selecciona la primera coincidencia QUE SE VE (mismo guard anti-TOGGLE).
  // La lista agrupa ausentes → incluidos → sobrantes; el orden del catálogo no
  // sirve (el sort es estable: dentro de cada grupo se conserva el del catálogo).
  const seleccionarPrimera = () => {
    const primera = [...coincidencias].sort(
      (a, b) => ORDEN_KIND.indexOf(a.kind) - ORDEN_KIND.indexOf(b.kind),
    )[0];
    if (primera != null && primera.id !== idSeleccion) seleccionar(primera.id);
  };

  // «Localiza»: desplaza la card seleccionada a la vista (no-op en jsdom).
  useEffect(() => {
    if (idSeleccion == null) return;
    const el = document.querySelector(`[data-cotejo-id="${CSS.escape(idSeleccion)}"]`);
    el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [idSeleccion]);

  // Contadores para CoverageBar y FilterRow
  const counts = useMemo(() => ({
    todos:     items.length,
    ausentes:  items.filter(i => i.kind === 'ausente').length,
    incluidos: items.filter(i => i.kind === 'incluido').length,
    sobrantes: items.filter(i => i.kind === 'sobrante').length,
  }), [items]);

  // P2VOC-c: P3 ya no monta barra de estado. La cuenta de tarjetas con y sin
  // coordenada sale una sola vez, bajo el mapa de apoyo (`MapaApoyoCotejo`).

  // Filtrado activo: vista + urgencia + texto (BUSCP3).
  const filtered = useMemo(
    () => (filtraPorTexto ? coincidencias : base),
    [filtraPorTexto, coincidencias, base],
  );

  // Aviso de lista vacía por el texto; «en esta vista» si el elemento existe pero
  // la pastilla o la urgencia lo ocultan.
  const hayCoincidenciaOculta = filtraPorTexto && filtered.length === 0
    && items.some((i) => coincide(i, qActual));

  const fAusentes  = filtered.filter(i => i.kind === 'ausente');
  const fIncluidos = filtered.filter(i => i.kind === 'incluido');
  const fSobrantes = filtered.filter(i => i.kind === 'sobrante');

  const handleAnadir = (id: string) => {
    const item = items.find(i => i.id === id);
    if (conAlmacen && onDecidir) {
      onDecidir(id, 'propuesto', 'catalogo');
    } else {
      setItems(prev => prev.map(i =>
        i.id === id ? { ...i, kind: 'incluido' } : i
      ));
    }
    if (item) {
      toast.success(`Añadido al PTEL`, {
        description: item.nombre,
        duration: 2500,
      });
    }
  };

  const handleIgnorar = (id: string) => {
    const item = items.find(i => i.id === id);
    if (conAlmacen && onDecidir) {
      // El ámbito sale del ítem tal como vino del cotejo: un ausente ya añadido
      // sigue siendo del catálogo (→ `descartado`); una fila del plan, `oculto` (DOP-4).
      const original = initialItems.find(i => i.id === id);
      const ambito = original ? ambitoDe(original) : 'plan';
      onDecidir(id, ambito === 'catalogo' ? 'descartado' : 'oculto', ambito);
    } else {
      setItems(prev => prev.filter(i => i.id !== id));
    }
    if (item) {
      toast.info(`Ignorado`, {
        description: item.nombre,
        duration: 2000,
      });
    }
  };

  // T3 · §6 — envoltorio de card con selección compartida: clic selecciona
  // (TOGGLE), la seleccionada se resalta con contorno (sin glow, regla dura).
  const renderCard = (item: CotejoItem, conAnadir: boolean) => {
    const seleccionada = item.id === idSeleccion;
    return (
      <div
        key={item.id}
        data-testid="coteja-card-wrap"
        data-cotejo-id={item.id}
        data-seleccionado={seleccionada || undefined}
        onClick={() => seleccionar(item.id)}
        style={{
          borderRadius: borderRadius.md,
          outline: seleccionada ? `2px solid ${colors.primary}` : 'none',
          outlineOffset: '2px',
        }}
      >
        <CandidatoCard
          item={item}
          onAnadir={conAnadir ? handleAnadir : undefined}
          onIgnorar={handleIgnorar}
        />
      </div>
    );
  };

  return (
    <div data-testid="step3-coteja-page" style={S.page}>

      {/* ── InfoBanner P3 (info azul) — 4.4 ── */}
      <div style={{ margin: '0 56px' }}>
        <InfoBanner
          variant="info"
          visible={bannerVisible}
          onClose={() => setBannerVisible(false)}
        >
          <BannerStrong>Cotejo completado.</BannerStrong>{' '}
          Revisa los elementos ausentes y confirma qué infraestructuras añadir al PTEL.
        </InfoBanner>
      </div>

      {/* T2b: context-bar retirada — municipio · provincia · INE viven en la
          fila 2 (WizardContextBar); el total de elementos lo expresan la
          resumen del mapa de apoyo y los contadores del FilterRow. */}

      {/* ── Espacio de trabajo map-céntrico (S.UI.A2a) — table-primary ── */}
      {/* La tabla de cotejo manda (ranura central); el mapa es apoyo lateral. */}
      <div style={S.shellWrap}>
        <WorkspaceShell
          alto="100%"
          anchoPanelIzq={300}
          panelIzq={<MapaApoyoCotejo ine5={ine5} items={items} municipio={municipio} idResaltado={idSeleccion} />}
          principal={
          <div style={S.centro}>
          <div style={S.content}>
        <div style={S.header}>
          <div style={S.title}>
            <GlossaryTooltip term="cotejo">Cotejo</GlossaryTooltip> de catálogo
          </div>
          <div style={S.subtitle}>
            Revisa los{' '}
            <GlossaryTooltip term="ausente">ausentes</GlossaryTooltip>{' '}
            y los{' '}
            <GlossaryTooltip term="sobrante">sobrantes</GlossaryTooltip>{' '}
            antes de exportar. Las urgentes se priorizan.
          </div>
        </div>

        {/* Indicador solo lectura viewer — 4.3 */}
        {!canEdit && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: `6px ${spacing[3]}`,
            marginBottom: spacing[3],
            background: colors.bg.tertiary,
            border: `1px solid ${colors.border.DEFAULT}`,
            borderRadius: borderRadius.md,
            fontSize: typography.fontSize.sm,
            color: colors.fg.muted,
            width: 'fit-content',
          }}>
            <svg viewBox="0 0 24 24" width={13} height={13} stroke="currentColor" strokeWidth={2} fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Solo lectura · Rol Viewer — puedes consultar pero no confirmar cambios
          </div>
        )}

        {/* CoverageBar — resuelve UI-03 */}
        <CoverageBar
          incluidos={counts.incluidos}
          ausentes={counts.ausentes}
          sobrantes={counts.sobrantes}
        />

        {/* FilterRow — resuelve UI-04, Urgencia a la derecha · buscador §6 (T3) */}
        <FilterRow
          vista={vista}
          onVista={setVista}
          urgencias={urgencias}
          onUrgencias={setUrgencias}
          counts={counts}
          buscador={
            <BuscadorGlobal
              valor={textoBusqueda}
              onCambio={manejarBusqueda}
              nCoincidencias={coincidencias.length}
              nombreSeleccionado={nombreSeleccionado}
              onSeleccionarPrimera={seleccionarPrimera}
            />
          }
        />

        {/* Listado de cards */}
        {filtered.length === 0 && filtraPorTexto ? (
          <div data-testid="coteja-sin-coincidencias" style={S.empty}>
            Ningún elemento coincide con “{textoBusqueda.trim()}”
            {hayCoincidenciaOculta ? ' en esta vista' : ''}.
          </div>
        ) : filtered.length === 0 ? (
          <div style={S.empty}>No hay resultados con los filtros activos.</div>
        ) : (
          <>
            {fAusentes.length > 0 && (
              <>
                <SectionDivider label="Ausentes" count={fAusentes.length} color={colors.error} />
                {fAusentes.map(item => renderCard(item, true))}
              </>
            )}
            {fIncluidos.length > 0 && (
              <>
                <SectionDivider label="Incluidos" count={fIncluidos.length} color={colors.success} />
                {fIncluidos.map(item => renderCard(item, false))}
              </>
            )}
            {fSobrantes.length > 0 && (
              <>
                <SectionDivider label="Sobrantes" count={fSobrantes.length} color={colors.info} />
                {fSobrantes.map(item => renderCard(item, false))}
              </>
            )}
          </>
        )}

        {/* ALBRTA-b1 · fuera del cotejo: no la filtran las pastillas ni mueve la cobertura. */}
        {registrosPlan && <AlojamientosRTASection ine5={ine5} registrosPlan={registrosPlan} />}

        {/* Footer de acciones */}
        <div data-testid="coteja-footer" style={S.footer}>
          <button data-testid="btn-coteja-back" type="button" style={S.btnBack} onClick={onBack}>← Volver</button>
          <button data-testid="btn-coteja-confirm" type="button" style={S.btnConfirm}
            onClick={() => {
              const confirmed = items.filter(i => i.kind === 'incluido');
              toast.success(
                `${confirmed.length} infraestructura${confirmed.length !== 1 ? 's' : ''} confirmada${confirmed.length !== 1 ? 's' : ''}`,
                { description: 'Preparando exportación…', duration: 3000 }
              );
              onConfirm(confirmed);
            }}>
            Confirmar y exportar →
          </button>
        </div>
          </div>
          </div>
          }
        />
      </div>
    </div>
  );
};
